import { CloseButton, Group, Tooltip } from '@mantine/core';
import { IconPencil, IconPlus, IconSearch, IconSquareArrowRight, IconTrash } from '@tabler/icons-react';
import { type QueryObserverResult } from '@tanstack/react-query';
import { isArray, uniq } from 'lodash';
import React, { useMemo } from 'react';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import { LabelNode } from '../../../../../../react-commons/components/inputs/getLabelNode';
import { getColorMantine } from '../../../../../../react-commons/components/inputs/text-fields/types';
import { ValidationEndAdornment } from '../../../../../../react-commons/components/ValidationEndAdornment';
import { ControlledInputs } from '../../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { formatIdentifier } from '../../../../../../react-commons/utils/utilFunctions';
import { type PersistableResource } from '../../../../resource/resource-base/persistableResourceTypes';
import { type PropertyDetailsResource } from '../../../../resource/resources/propertyDetailsResource';
import { type PropertyValidation } from '../../../../resource/services/outlineTypes';
import { TOOLTIPS } from '../../../../resource/tooltipTexts';
import { compareAlphanumerically, getPropertyLabelInfo } from '../../../util/utilFunctions';
import { DisableableIconButtonWithTooltip } from '../../DisableableIconButtonWithTooltip';

type LocalArtifact = PersistableResource & {
    objectId: string;
};

type PersistableWithDescription = PersistableResource & {
    description: string;
};

// TODO T extends PersistableResource, P extends T
export const Controlled_AutocompleteReferenceTextField = <P extends PersistableResource>({
    idValue,
    propertyDetails,
    propertyValidation,
    query,
    label,
    onGoToClick,
    getOptionLabel,
    getOptionString,
    groupBy,
    getToolTip,
    onChange,
    filterResources,
    readOnly,
    disabled,
    onEdit,
    onAdd,
    onCloseButtonClick,
    onDelete,
    onSearch,
    idSelector = 'objectId',
    customPlaceHolder,
    internalLabelWhenNotSet,
    sortOptionsBy,
    hideLabel = false,
    notNullable = false,
}: {
    idValue: string | undefined | null;
    label?: string;
    handleChange?: (id: string | null) => void;
    propertyDetails: PropertyDetailsResource | PropertyDetailsResource[] | undefined;
    propertyValidation: PropertyValidation | null | undefined;
    onGoToClick?: (resourceId: string | undefined, openInNewTab: boolean) => void;
    getOptionLabel: (resource: P) => string;
    getOptionString?: (resource: P) => string;
    filterResources?: (resources: P[], selectedResource: P | null) => P[];
    readOnly?: boolean;
    query: QueryObserverResult<P | P[] | { content: P } | { content: P[] } | null>;
    groupBy?: (resource: P) => string;
    getToolTip?: (resource: P | undefined) => React.ReactElement | undefined;
    onChange?: (value: string | null) => void;
    disabled?: boolean;
    onEdit?: () => void;
    onAdd?: () => void;
    onDelete?: () => void;
    onSearch?: () => void;
    onCloseButtonClick?: () => void;
    idSelector?: 'id' | 'objectId';
    customPlaceHolder?: string;
    internalLabelWhenNotSet?: string | React.ReactNode;
    hideLabel?: boolean;
    notNullable?: boolean;
    sortOptionsBy?: (a: P, b: P) => number;
}) => {
    const labelFunction = getOptionString ?? getOptionLabel; // TODO only allow one of these
    const severity = propertyValidation?.severity;
    const helperText = propertyValidation?.identifier;

    const queryData = useMemo(
        () =>
            query.data?.let((it) => {
                if ('content' in it) {
                    return it.content as P | P[];
                } else {
                    return it;
                }
            }),
        [query.data]
    );

    const optionsArray = useMemo(() => {
        const array =
            Array.isArray(queryData) ? queryData
            : queryData ? [queryData]
            : [];

        array.toSorted((a, b) =>
            sortOptionsBy ? sortOptionsBy(a, b) : compareAlphanumerically(labelFunction(a), labelFunction(b))
        );
        if (groupBy) {
            array.toSorted((a, b) => groupBy(a).localeCompare(groupBy(b)));
        }
        if (filterResources) {
            const selectedResource = array.find((r) => {
                if (idSelector === 'objectId') {
                    return (r as LocalArtifact).objectId === idValue;
                } else {
                    return r.id === idValue;
                }
            });
            return filterResources(array, selectedResource ?? null);
        }
        return array;
    }, [queryData, filterResources]);

    const resource = useMemo(() => {
        return optionsArray.find((r) => {
            if (idSelector === 'objectId') {
                return (r as LocalArtifact).objectId === idValue;
            } else {
                return r.id === idValue;
            }
        });
    }, [optionsArray, idValue, idSelector]);

    const isDisabled = disabled ? true : !queryData;

    const data = useMemo(() => {
        const getResourceValue = (resource: P) => {
            if (idSelector === 'objectId' && 'objectId' in resource) {
                return (resource.objectId as string) ?? '';
            } else {
                return resource.id ?? '';
            }
        };

        const getResourceDescription = (resource: P) => {
            const description = (resource as PersistableWithDescription).description;
            return Boolean(description) ? description : undefined;
        };

        if (groupBy) {
            const individuallyGrouped = optionsArray.map((value) => ({
                group: groupBy(value),
                item: {
                    value: getResourceValue(value),
                    label: labelFunction(value),
                    description: getResourceDescription(value),
                },
            }));
            const groupsFound = uniq(individuallyGrouped.map((value) => value.group));
            return groupsFound
                .map((groupValue) => ({
                    group: groupValue,
                    items: individuallyGrouped
                        .filter((value) => value.group === groupValue)
                        .map((value) => value.item)
                        .toSorted((a, b) => a.label.localeCompare(b.label)),
                }))
                .toSorted((a, b) => a.group.localeCompare(b.group));
        }
        const tempData = optionsArray.map((value) => ({
            value: getResourceValue(value),
            label: labelFunction(value),
            description: getResourceDescription(value),
        }));
        if (!sortOptionsBy) {
            return tempData.toSorted((a, b) => a.label.localeCompare(b.label));
        }
        return tempData;
    }, [optionsArray, idSelector]);

    const getRightSectionWidth = () => {
        let currentWidth = 29.5;
        if (onEdit) {
            currentWidth = currentWidth + 29.5;
        }
        if (resource !== undefined && !readOnly && !notNullable) {
            currentWidth = currentWidth + 29.5;
        }
        if (severity && helperText) {
            currentWidth = currentWidth + 29.5;
        }
        return currentWidth;
    };

    const getOuterRightSectionWidth = () => {
        let currentWidth = 0;
        if (onAdd) {
            currentWidth = currentWidth + 30;
        }
        if (onSearch) {
            currentWidth = currentWidth + 30;
        }
        if (onDelete) {
            currentWidth = currentWidth + 30;
        }
        return currentWidth;
    };

    const autoCompleteContent = (
        <ControlledInputs.Reference
            value={idValue ?? null}
            nullable={!readOnly}
            onChange={onChange}
            placeholder={resource === undefined && idValue ? idValue : (customPlaceHolder ?? 'not defined')}
            label={
                !hideLabel && (
                    <LabelNode
                        labelInfo={getPropertyLabelInfo(propertyDetails)}
                        label={
                            isArray(propertyDetails) ?
                                (propertyDetails[0]?.displayName ?? formatIdentifier(propertyDetails[0]?.propertyName))
                            : propertyDetails ?
                                (propertyDetails?.displayName ?? formatIdentifier(propertyDetails.propertyName))
                            :   label
                        }
                    />
                )
            }
            readOnly={readOnly ?? false}
            data={data}
            disabled={disabled}
            size={'sm'}
            styles={{
                root: {
                    pointerEvents: disabled ? 'none' : undefined,
                    width: '100%',
                },
                input: {
                    borderColor: getColorMantine(severity),
                    backgroundColor: readOnly ? 'var(--mantine-color-read-only-input)' : undefined,
                },
            }}
            rightSectionWidth={getRightSectionWidth()}
            rightSection={
                <Group
                    justify={'flex-end'}
                    gap={4}
                    style={{
                        paddingRight: 4,
                        alignItems: 'center',
                    }}
                    w={'100%'}
                >
                    <Show when={!resource}>{internalLabelWhenNotSet}</Show>
                    <Show when={resource && !readOnly && !notNullable}>
                        <CloseButton
                            size="md"
                            variant="transparent"
                            onClick={() => {
                                if (onChange && onCloseButtonClick) {
                                    onCloseButtonClick();
                                } else if (onChange) {
                                    onChange(null);
                                }
                            }}
                        />
                    </Show>
                    <Show when={onEdit}>
                        <DisableableIconButtonWithTooltip
                            disabled={isDisabled || (!resource && disabled === undefined)}
                            icon={<IconPencil />}
                            tooltipText={TOOLTIPS.EDIT_REFERENCE}
                            onClick={onEdit}
                        />
                    </Show>
                    <Show when={onGoToClick}>
                        <DisableableIconButtonWithTooltip
                            disabled={isDisabled || !resource}
                            onClick={() => {
                                if (onGoToClick) {
                                    onGoToClick(resource?.id, false);
                                }
                            }}
                            onAuxClick={(event) => {
                                if (event.button === 1) {
                                    if (onGoToClick) {
                                        onGoToClick(resource?.id, true);
                                    }
                                }
                            }}
                            tooltipText={TOOLTIPS.GO_TO_REFERENCE}
                            icon={<IconSquareArrowRight />}
                        />
                    </Show>
                    <ValidationEndAdornment severity={severity} helperText={helperText ? [helperText] : undefined} />
                </Group>
            }
        />
    );

    const tooltip = getToolTip?.(resource);

    return (
        <div style={{ display: 'inline-flex', width: '100%', minWidth: 112 }}>
            <Show when={tooltip} fallback={autoCompleteContent}>
                <Tooltip label={tooltip}>{autoCompleteContent}</Tooltip>
            </Show>
            <Show when={!readOnly}>
                <Group
                    miw={getOuterRightSectionWidth()}
                    gap={6}
                    style={{
                        flexGrow: 1,
                        display: 'inline-flex',
                        paddingTop: !hideLabel ? 26 : 0,
                        paddingLeft: 4,
                        alignItems: 'center',
                    }}
                >
                    <Show when={onSearch}>
                        <DisableableIconButtonWithTooltip
                            disabled={isDisabled}
                            tooltipText={TOOLTIPS.SEARCH_FOR_REFERENCE}
                            icon={<IconSearch />}
                            onClick={onSearch}
                        />
                    </Show>
                    <Show when={onAdd}>
                        <DisableableIconButtonWithTooltip
                            disabled={isDisabled}
                            tooltipText={TOOLTIPS.CREATE_NEW_REFERENCE}
                            icon={<IconPlus />}
                            onClick={onAdd}
                        />
                    </Show>
                    <Show when={onDelete}>
                        <DisableableIconButtonWithTooltip
                            disabled={isDisabled}
                            tooltipText={TOOLTIPS.DELETE}
                            icon={<IconTrash />}
                            onClick={onDelete}
                        />
                    </Show>
                </Group>
            </Show>
        </div>
    );
};
